// THE FLY START SCREEN — an empty session's first screen, made from the
// workspace on screen (functions/assistantFlyStart.js; Nika 2026-10-22: "give
// fly initial screen to start from based on each workspace", then "make fly
// same width as dashboard. fly should first show current summary when its
// empty on each workspace according to their data").
//
//   the summary  FIRST: the day and the workspace · the home's greeting ·
//                where the workspace stands — the dashboard's own summary
//                (HomeSummaryBrief: the signals' sentences, AI or template,
//                each fact a link to its list; Updated · Refresh), or one calm
//                line when nothing needs the person
//   the asks     "Ask <workspace> for the page you need." · one line on what
//                Fly does · the tiles — live FIGURES (what needs the person,
//                from the installed apps' signals) and the workspace's own
//                ASKS (its starters), the first the large one, as many as fill
//                whole rows on the dashboard's columns
//   the more     Next up · Recent — what comes next, the pages put away (an
//                empty part leaves no gap)
//   first run    on a NEW workspace only: "Get started", four steps that
//                mirror the activation rule (components/AssistantFlyFirstRun.js;
//                last, so its late read moves nothing above it)
//
// No tools row (Nika 2026-10-23: "open workspace tools is already in top menu
// so whats the point?"): the top menu opens every app, and Add section is a
// row of the composer's "+" menu (functions/assistantFlyWorkspace.js
// assistantFlySectionOffered).
//
// As wide as the dashboard: the view's grid (functions/assistantFly.js
// assistantFlyMeasure) gives the tiles their tracks; text keeps its reading
// measure. It holds no logic: every value comes from functions/
// assistantFlyStart.js through `el.call`. It shows while the canvas is open
// with no page; the first ask replaces it with the page (the view's own
// crossfade). It never moves a tile it already shows: the slots hold
// skeletons until the first signals pass has answered (Rule 66).
export const AssistantFlyStart = {
  tag: 'section',
  dataFlyStart: true,
  hide: (el, s) => !el.call('assistantFlyStartShown', s.root),
  flow: 'y',
  gap: 'C',
  width: '100%',
  marginInline: 'auto',
  minWidth: '0',
  boxSizing: 'border-box',
  animation: 'fadeInUpShort',
  animationDuration: 'C',
  animationTimingFunction: 'defaultBezier',
  // What the screen needs is asked only while it SHOWS (it stays mounted,
  // hidden, on every home): at mount when it shows at once, then whenever it
  // comes back, the workspace becomes ready, its apps land or the member's
  // preferences row is read — the cached starter set lives there, so it is
  // judged only once the row has landed (STARTERS-ONCE). The framework's
  // declarative effect never fires on the first render, hence onRender.
  onRender: (el, s) => el.call('assistantFlyStartPrime', s.root),
  stateDeps: [
    (el, s) => !!el.call('assistantFlyStartShown', s.root),
    (el, s) => !!(s.root && s.root.workspaceReady),
    (el, s) => (s.root && Array.isArray(s.root.workspaceApps) ? s.root.workspaceApps.length : -1),
    (el, s) => !!(s.root && s.root.userPrefs && typeof s.root.userPrefs === 'object')
  ],
  onStateUpdate: (el, s) => el.call('assistantFlyStartPrime', s.root),

  // ── THE SUMMARY — where this workspace stands, before anything is asked ───
  FlyStartHead: {
    tag: 'header',
    flow: 'y',
    gap: 'Z',
    minWidth: '0',
    boxSizing: 'border-box',
    padding: 'B',
    round: 'radiusCard',
    background: (el) =>
      el.call('assistantFlyOnHome', el)
        ? 'var(--home-card-bg, var(--theme-card-background))'
        : 'cardSolid',
    '@mobileL': { padding: 'A' },

    // The day and the workspace, in the page eyebrow's voice.
    FlyStartEyebrow: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z',
      fontWeight: '500',
      lineHeight: '1.3',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'inactive',
      text: (el, s) => {
        const day = el.call('localizedDate', Date.now(), {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        })
        const scope = String(el.call('assistantScopeName', s.root) || '')
        return scope ? day + ' · ' + scope : String(day || '')
      }
    },

    // The workspace greeting anchors the summary before the adaptive asks.
    // A line, not a heading (the page's one h1 is the shell's).
    FlyStartGreeting: {
      tag: 'p',
      margin: '0',
      fontSize: 'C',
      fontWeight: '400',
      lineHeight: '1.25',
      color: 'title',
      overflowWrap: 'anywhere',
      GreetingLead: {
        tag: 'span',
        text: (el, s) => (el.call('assistantFlyStartGreeting', s) || {}).lead || ''
      },
      GreetingName: {
        tag: 'span',
        fontWeight: '700',
        text: (el, s) => (el.call('assistantFlyStartGreeting', s) || {}).name || ''
      }
    },

    // Nothing to summarise: the calm line (the summary keeps its Updated ·
    // Refresh row under it).
    FlyStartCalm: {
      tag: 'p',
      dataFlyStartCalm: true,
      margin: '0',
      maxWidth: 'I',
      fontSize: 'A',
      lineHeight: '1.5',
      color: 'caption',
      hide: (el, s) => !el.call('assistantFlyStartCalm', s.root),
      text: (el, s) => el.call('assistantFlyStartCalm', s.root)
    },

    // The dashboard's own summary: the same model, the same sentences and
    // links, its skeleton while the signals answer. Its figures are the tiles
    // below (each one a page), so the AI sentence's own chip row and the
    // board's "Open as board" stay on the dashboard.
    HomeSummaryBrief: {
      dataFlyStartSummary: true,
      BriefChips: { hide: () => true },
      BriefMeta: { AppCapsuleBtnGhost_board: { hide: () => true } }
    }
  },

  // ── THEN WHAT TO ASK ──────────────────────────────────────────────────────
  FlyStartAsks: {
    flow: 'y',
    gap: 'A',
    minWidth: '0',

    FlyStartIntro: {
      flow: 'y',
      gap: 'Y',
      minWidth: '0',
      // One text node keeps the word boundary in both the DOM and spoken name.
      FlyStartTitle: {
        tag: 'h2',
        margin: '0',
        fontSize: 'B',
        fontWeight: '700',
        lineHeight: '1.3',
        color: 'title',
        overflowWrap: 'anywhere',
        text: (el, s) =>
          String(el.call('polyglot', 'workspace.flyStart.titleLead')).replace(
            '{workspace}',
            String(el.call('assistantScopeName', s.root) || '')
          ) +
          ' ' +
          String(el.call('polyglot', 'workspace.flyStart.titleRest'))
      },

      FlyStartLead: {
        tag: 'p',
        margin: '0',
        maxWidth: 'H',
        fontSize: 'Z2',
        lineHeight: '1.5',
        color: 'caption',
        text: '{{ workspace.flyStart.lead | polyglot }}'
      }
    },

    // The tiles: the dashboard's columns (three to six; two on a phone), the
    // large tile two by two beside the smaller ones, packed densely. Every
    // row is as tall as the tallest (`1fr` in a grid of no set height), so the
    // tiles line up in even rows whatever their words.
    FlyStartTiles: {
      role: 'group',
      dataFlyStartTiles: true,
      ariaLabel: (el) => el.call('polyglot', 'workspace.flyStart.tilesLabel'),
      display: 'grid',
      style: (el, s) => ({ '--fly-start-cols': String(el.call('assistantFlyStartTracks', s)) }),
      gridTemplateColumns: 'repeat(var(--fly-start-cols, 3), minmax(0, 1fr))',
      gridAutoRows: 'minmax(var(--spacing-E), 1fr)',
      gridAutoFlow: 'row dense',
      gap: 'A',
      minWidth: '0',
      '@mobileL': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
      children: (el, s) =>
        el.call('assistantFlyStartTiles', s.root, el.call('assistantFlyStartTracks', s)),
      childrenAs: 'state',
      childExtends: 'AssistantFlyStartTile'
    }
  },

  // ── WHAT IS NEXT, WHAT YOU ASKED (Nika 2026-10-23: "make fly starter screen
  // more advanced", "expand fly empty screen functionality", then "remove
  // members from init fly. make better design for recent") ─────────────────
  //   Next up  what is next for the person — the dashboard's Next up: their
  //            events and the installed apps' upcoming items (a viewing, …):
  //            when, what, its times; one press joins a call, or opens it
  //            (functions/assistantFlyStart.js assistantFlyStartNext)
  //   Recent   the pages a fresh Fly put away (the logo, the home icon, New),
  //            newest first, as cards of the tiles' family: a press asks
  //            again — a new page on live data
  // Side by side where they fit, one under the other on a narrow screen; a
  // part with nothing in it leaves no gap. (Members left the screen: a call
  // is still one ask away — "call Nika", functions/assistantFlyPeople.js.)
  FlyStartMore: {
    dataFlyStartMore: true,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-H)), 1fr))',
    gap: 'B',
    minWidth: '0',
    // the apps' upcoming items (Next up's providers' pass,
    // assistantFlyStartNextCollect) — every key declared, so a read never
    // falls back to a root key of the same name
    state: { upcoming: null },
    onRender: (el, s) => el.call('assistantFlyStartNextCollect', el, s),
    stateDeps: [
      (el, s) => (s.root ? s.root._meetingInviteTick : null),
      (el, s) => !!el.call('assistantFlyStartShown', s.root),
      (el, s) => (s.root && Array.isArray(s.root.workspaceApps) ? s.root.workspaceApps.length : -1)
    ],
    onStateUpdate: (el, s) => el.call('assistantFlyStartNextCollect', el, s),
    hide: (el, s) =>
      !(el.call('assistantFlyStartNext', s.root, 3, s.upcoming) || []).length &&
      !(el.call('assistantFlyStartRecent', s.root) || []).length,

    FlyStartNext: {
      tag: 'section',
      dataFlyStartNext: true,
      flow: 'y',
      gap: 'Z',
      minWidth: '0',
      ariaLabel: (el) => el.call('polyglot', 'workspace.widget.nextUp.title'),
      hide: (el, s) => !(el.call('assistantFlyStartNext', s.root, 3, s.upcoming) || []).length,
      AssistantFlyStartPartTitle: { text: '{{ workspace.widget.nextUp.title | polyglot }}' },
      FlyStartNextList: {
        role: 'list',
        flow: 'y',
        gap: 'X',
        minWidth: '0',
        // "in 12 min" moves with the shell's 30 s tick; the calendar window
        // brings new events
        childrenDeps: ['intranetEvents', '_meetingInviteTick'],
        children: (el, s) => el.call('assistantFlyStartNext', s.root, 3, s.upcoming),
        childrenAs: 'state',
        childExtends: 'AssistantFlyStartEvent'
      }
    },

    FlyStartRecent: {
      tag: 'section',
      dataFlyStartRecent: true,
      flow: 'y',
      gap: 'Z',
      minWidth: '0',
      ariaLabel: (el) => el.call('polyglot', 'workspace.aiScope.group.recent'),
      hide: (el, s) => !(el.call('assistantFlyStartRecent', s.root) || []).length,
      AssistantFlyStartPartTitle: { text: '{{ workspace.aiScope.group.recent | polyglot }}' },
      // Cards of one width on the part's own tracks — as many as fit, a few
      // pages never stretch across the screen (empty tracks stay empty); two
      // on a phone, like the tiles. Every row as tall as its tallest card.
      FlyStartRecentList: {
        role: 'list',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, var(--spacing-G)), 1fr))',
        gridAutoRows: '1fr',
        gap: 'Z',
        minWidth: '0',
        '@mobileL': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
        children: (el, s) => el.call('assistantFlyStartRecent', s.root),
        childrenAs: 'state',
        childExtends: 'AssistantFlyStartRecentCard'
      }
    }
  },

  // ── A PERSONALIZED SIGN-UP'S FIRST THREAD (POB S7) ───────────────────────
  // what research read and from where, the three offers (Website · Team ·
  // Imports) and "Getting started" — only in the workspace of a personalized
  // sign-up (functions/assistantFlyResearch.js); the generic first steps
  // below step aside there
  AssistantFlyResearch: {},

  // ── A NEW WORKSPACE'S FIRST STEPS (fly docs/FUNNEL.md §4 G5) ──────────────
  // "Get started": invite · records · website · ask, from the server's
  // activation read; hidden everywhere else (functions/assistantFlyFirstRun.js)
  AssistantFlyFirstRun: {}
}

// THE START SCREEN'S PRESS — the one pressable surface of its tiles, its
// events, its recent pages and the canvas's folded rows: the house
// card over the home's sky (on the page layer over another route the ground is
// a sheet, where the glass card would vanish in the light theme — there the
// solid card, AssistantFlyCard's own rule), its states a layer over that card —
// hover and press an inset wash in the CARD's tokens (`cardHover` /
// `cardPress`: a step darker in both themes; the home's signal tile's way: the
// card's fill is the skin's glass, so the state is never an opacity of the
// label), keyboard focus the house ring.
export const AssistantFlyStartPress = {
  tag: 'button',
  type: 'button',
  position: 'relative',
  minWidth: '0',
  boxSizing: 'border-box',
  borderWidth: '0',
  background: (el) =>
    el.call('assistantFlyOnHome', el)
      ? 'var(--home-card-bg, var(--theme-card-background))'
      : 'cardSolid',
  color: 'title',
  textAlign: 'left',
  fontFamily: 'inherit',
  cursor: 'pointer',
  touchAction: 'manipulation',
  // the wash lies UNDER the words (its own stacking context, the layer at -1):
  // a hover darkens the card's fill, never its text — a press holds no layer
  // that must paint over its neighbours
  isolation: 'isolate',
  '::after': {
    content: '""',
    position: 'absolute',
    inset: '0',
    zIndex: '-1',
    borderRadius: 'inherit',
    background: 'cardHover',
    opacity: '0',
    pointerEvents: 'none',
    transition: 'opacity A defaultBezier'
  },
  '@fine': { ':hover:not([disabled])::after': { opacity: '1' } },
  ':active:not([disabled])::after': { opacity: '1', background: 'cardPress' },
  ':focus-visible': { shadow: 'focusRing' }
}

// ONE START TILE — a button that asks its page (assistantFlyStartAsk). Its
// state is the tile (functions/assistantFlyStart.js assistantFlyStartTiles):
//   figure   the count large, its words, the app with a severity dot (and
//            "+n more" for the rest of its group), then its ask
//   ask      the app's calm glyph and name, then the ask
//   skeleton the slot's place while the first signals pass answers
// The first tile is the large one (two tracks by two rows on a wide screen).
// The state reaches the CSS as attributes (`data-fly-start-tile`, `data-hero`,
// `data-severity`), never as a create-only case block.
export const AssistantFlyStartTile = {
  extends: 'AssistantFlyStartPress',
  dataFlyStartTile: (el, s) => (s && s.kind) || null,
  dataHero: (el, s) => (s && s.hero ? 'true' : null),
  dataTall: (el, s) => (s && s.tall ? 'true' : null),
  dataSeverity: (el, s) => (s && s.kind === 'figure' && s.severity) || null,
  disabled: (el, s) => (!s || s.kind === 'skeleton' ? true : null),
  ariaBusy: (el, s) => (!s || s.kind === 'skeleton' ? 'true' : null),
  ariaLabel: (el, s) => el.call('assistantFlyStartLabel', s) || null,
  title: (el, s) => (s && s.kind !== 'skeleton' && s.prompt !== s.text ? s.prompt : null),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: 'Z',
  padding: 'A1',
  round: 'radiusCard',
  // WHOLE ROWS (functions/assistantFlyStart.js `shape`): a tile spans the
  // tracks its row gives it — the large tile two (a lone one the row), the
  // last one what its row has left — and the large tile is two rows tall
  // (`tall`) only beside a full first row, from the tablet step up: on a phone
  // it fills its row, and two even rows made it a 416 px block (dev, 390 px).
  // A phone's two tracks span nothing else.
  style: (el, s) => ({ '--fly-tile-span': String((s && s.span) || 1) }),
  gridColumn: 'span var(--fly-tile-span, 1)',
  '@mobileL<': { '&[data-tall="true"]': { gridRow: 'span 2' } },
  '@mobileL': { gridColumn: 'auto', '&[data-hero="true"]': { gridColumn: 'span 2' } },
  '&[data-fly-start-tile="skeleton"]': { cursor: 'default' },
  onClick: (e, el, s) => el.call('assistantFlyStartAsk', s),

  // ── The figure (a figure tile) or the app (an ask tile) ───────────────────
  TileHead: {
    flow: 'x',
    align: 'center',
    flexWrap: 'wrap',
    gap: 'Y',
    minWidth: '0',
    hide: (el, s) => !s || s.kind === 'skeleton',
    // A figure reads as one line — the count and its words on one baseline.
    ':is([data-fly-start-tile="figure"]) &': { alignItems: 'baseline' },
    TileNumeral: {
      tag: 'span',
      hide: (el, s) => !s || s.kind !== 'figure',
      flexShrink: '0',
      fontSize: 'C',
      fontWeight: '700',
      lineHeight: '1',
      fontVariantNumeric: 'tabular-nums',
      // The large tile's count is the screen's headline figure.
      ':is([data-fly-start-tile][data-hero="true"]) &': { fontSize: 'H' },
      text: (el, s) => (s && s.count) || ''
    },
    TileWords: {
      tag: 'span',
      hide: (el, s) => !s || s.kind !== 'figure',
      minWidth: '0',
      fontSize: 'Z2',
      lineHeight: '1.35',
      color: 'caption',
      text: (el, s) => (s && s.words) || ''
    },
    AppGlyphTile: {
      hide: (el, s) => !s || s.kind !== 'ask'
    },
    TileApp: {
      tag: 'span',
      hide: (el, s) => !s || s.kind !== 'ask' || !s.app,
      minWidth: '0',
      fontSize: 'Z2',
      color: 'caption',
      text: (el, s) => (s && s.app) || ''
    }
  },

  // A figure's app, with its severity: a dot in the danger ink (urgent), the
  // warning ink (attention) or the caption ink (information) — the words of
  // the severity are in the tile's name.
  TileMeta: {
    flow: 'x',
    align: 'center',
    gap: 'Y',
    minWidth: '0',
    hide: (el, s) => !s || s.kind !== 'figure' || (!s.app && !s.more),
    fontSize: 'Z',
    color: 'caption',
    TileDot: {
      tag: 'span',
      ariaHidden: 'true',
      boxSize: 'Y',
      flexShrink: '0',
      round: 'radiusPill',
      background: 'currentColor',
      ':is([data-fly-start-tile][data-severity="urgent"]) &': { background: 'dangerInk' },
      ':is([data-fly-start-tile][data-severity="attention"]) &': { background: 'warningInk' }
    },
    TileMetaText: {
      tag: 'span',
      minWidth: '0',
      text: (el, s) => {
        if (!s) return ''
        return [s.app, s.more].filter(Boolean).join(' · ')
      }
    }
  },

  // ── The ask ────────────────────────────────────────────────────────────────
  TileFoot: {
    flow: 'x',
    align: 'flex-end space-between',
    gap: 'Z',
    minWidth: '0',
    marginTop: 'auto',
    // A phone's half-row tile left the ask 116px beside the arrow, and a long
    // Georgian word (გასაკეთებელი? — 128px) broke in the middle (390px, dev
    // 2026-10-23): there the arrow steps under the ask, which takes the tile's
    // whole width.
    '@mobileL': { flexDirection: 'column', alignItems: 'flex-end', gap: 'Y' },
    hide: (el, s) => !s || s.kind === 'skeleton',
    TileAsk: {
      tag: 'span',
      minWidth: '0',
      fontSize: 'A',
      fontWeight: '500',
      lineHeight: '1.3',
      overflowWrap: 'anywhere',
      ':is([data-fly-start-tile][data-hero="true"]) &': { fontSize: 'C', fontWeight: '700' },
      '@mobileL': { alignSelf: 'stretch' },
      text: (el, s) => (s && s.text) || ''
    },
    Icon: { name: 'arrowRight', boxSize: 'iconSm', flexShrink: '0' }
  },

  TileSkeleton: {
    flow: 'y',
    gap: 'Y',
    width: '100%',
    hide: (el, s) => !!s && s.kind !== 'skeleton',
    Skeleton_1: { width: '38%', height: 'B' },
    Skeleton_2: { width: '86%', height: 'A' },
    Skeleton_3: { width: '58%', height: 'A' }
  }
}

// A part's title on the start screen (Next up, Recent): the eyebrow's voice, a
// heading under the page's own.
export const AssistantFlyStartPartTitle = {
  tag: 'h3',
  margin: '0',
  fontSize: 'Z',
  fontWeight: '500',
  lineHeight: '1.3',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'inactive'
}

// ONE NEXT EVENT — a row: when ("in 12 min", "now", the weekday), the title
// and its times, its action as a pill — Join when it has a call, else Open;
// the whole row is the one press (assistantFlyStartEventOpen). Its name for
// assistive tech says all of it.
export const AssistantFlyStartEvent = {
  extends: 'AssistantFlyStartPress',
  role: 'listitem',
  dataFlyStartEvent: (el, s) => (s && s.key) || null,
  ariaLabel: (el, s) =>
    s
      ? [s.when, s.title, s.time].filter(Boolean).join(' · ') +
        ' — ' +
        String(el.call('polyglot', s.label || 'common.action.open'))
      : null,
  display: 'flex',
  alignItems: 'center',
  gap: 'Z',
  padding: 'Z A',
  round: 'radiusCard',
  onClick: (e, el, s) => el.call('assistantFlyStartEventOpen', s),
  EventWhen: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Z2',
    fontWeight: '700',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    text: (el, s) => (s && s.when) || ''
  },
  EventText: {
    flow: 'y',
    gap: 'X',
    flex: '1',
    minWidth: '0',
    EventTitle: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '500',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => (s && s.title) || ''
    },
    EventTime: {
      tag: 'span',
      fontSize: 'Z',
      color: 'inactive',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => (s && s.time) || ''
    }
  },
  EventAction: {
    tag: 'span',
    flexShrink: '0',
    padding: 'X Z',
    round: 'radiusPill',
    background: 'selected',
    fontSize: 'Z',
    fontWeight: '500',
    text: (el, s) => String(el.call('polyglot', (s && s.label) || 'common.action.open'))
  }
}

// ONE RECENT PAGE — a row: its title, when it was made, the "ask again"
// arrow; a press asks it again — a new page on live data
// (assistantFlyStartAsk). Its full ask is the hover title when the title
// shortens it.
export const AssistantFlyStartRecentPage = {
  extends: 'AssistantFlyStartPress',
  role: 'listitem',
  ariaLabel: (el, s) =>
    String(el.call('polyglot', 'workspace.assistantFly.again')) + ': ' + ((s && s.title) || ''),
  title: (el, s) => (s && s.prompt && s.prompt !== s.title ? s.prompt : null),
  display: 'flex',
  alignItems: 'center',
  gap: 'Z',
  padding: 'Z A',
  round: 'radiusCard',
  onClick: (e, el, s) => el.call('assistantFlyStartAsk', s),
  RecentTitle: {
    tag: 'span',
    flex: '1',
    minWidth: '0',
    fontSize: 'Z2',
    fontWeight: '500',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => (s && s.title) || ''
  },
  RecentWhen: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Z',
    color: 'inactive',
    text: (el, s) => (s && s.at ? String(el.call('timeAgo', s.at) || '') : '')
  },
  Icon: { name: 'arrowRight', boxSize: 'iconSm', flexShrink: '0', color: 'caption' }
}

// ONE RECENT PAGE on the start screen — a card of the tiles' family (Nika
// 2026-10-23: "make better design for recent"): the history glyph and when it
// was made on top, in the tile head's caption voice; the page's title at the
// foot, in the ask's own size, two lines at most, the "ask again" arrow beside
// it. The row's parts and its press (AssistantFlyStartRecentPage), placed on a
// grid: the reading order stays the title first, the card shows it last.
export const AssistantFlyStartRecentCard = {
  extends: 'AssistantFlyStartRecentPage',
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  gridTemplateRows: 'auto 1fr',
  gridTemplateAreas: '"mark when ." "title title go"',
  alignItems: 'center',
  columnGap: 'Y',
  rowGap: 'B',
  padding: 'A',
  round: 'radiusCard',
  RecentTitle: {
    gridArea: 'title',
    alignSelf: 'end',
    fontSize: 'A',
    lineHeight: '1.35',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineClamp: '2'
  },
  RecentWhen: {
    gridArea: 'when',
    minWidth: '0',
    color: 'caption',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  Icon: { gridArea: 'go', alignSelf: 'end' },
  Icon_history: {
    gridArea: 'mark',
    ariaHidden: 'true',
    name: 'sfHistory',
    boxSize: 'iconSm',
    color: 'caption'
  }
}
